import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const MLOpsFramework = () => {
  return (
    <>
      <SEO 
        title="MLOps Framework - Enterprise ML Operations & Deployment"
        description="Streamline your machine learning operations with Abacies' comprehensive MLOps framework. From model development to production deployment, monitoring, and governance."
        keywords="MLOps, machine learning operations, ML deployment, model monitoring, ML pipeline, CI/CD for ML, model governance, AI operations"
      />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 bg-gradient-to-br from-pearl-white via-white to-blush-light/20 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-10 w-72 h-72 bg-coral-mist/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-primary-red/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="page-container relative z-10">
          <div className="max-w-5xl mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center space-x-2 text-sm text-titanium mb-6 animate-fade-in">
              <Link to="/" className="hover:text-primary-red transition-colors">Home</Link>
              <span>/</span>
              <Link to="/ai-hub" className="hover:text-primary-red transition-colors">AI Hub</Link>
              <span>/</span>
              <span className="text-charcoal font-medium">MLOps Framework</span>
            </div>

            <div className="text-center">
              {/* Badge */}
              <div className="inline-flex items-center px-4 py-2 bg-white rounded-full shadow-sm border border-platinum mb-6 animate-slide-up">
                <svg className="w-5 h-5 text-primary-red mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span className="text-sm font-semibold text-charcoal">Enterprise ML Operations</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-slide-up" style={{ animationDelay: '0.1s' }}>
                MLOps <span className="text-gradient">Framework</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-titanium mb-8 leading-relaxed max-w-4xl mx-auto animate-slide-up" style={{ animationDelay: '0.2s' }}>
                Transform your ML lifecycle with enterprise-grade operations. Deploy, monitor, and scale machine learning models with confidence and efficiency.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 animate-slide-up" style={{ animationDelay: '0.3s' }}>
                <Link to="/contact" className="btn-primary text-center">
                  Request Demo
                </Link>
                <Link to="/ai-hub" className="btn-secondary text-center">
                  Explore AI Hub
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto animate-slide-up" style={{ animationDelay: '0.4s' }}>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-platinum shadow-sm">
                  <div className="text-3xl font-bold text-primary-red mb-1">85%</div>
                  <div className="text-sm text-titanium">Faster Deployment</div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-platinum shadow-sm">
                  <div className="text-3xl font-bold text-primary-red mb-1">99.9%</div>
                  <div className="text-sm text-titanium">Model Uptime</div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-platinum shadow-sm">
                  <div className="text-3xl font-bold text-primary-red mb-1">60%</div>
                  <div className="text-sm text-titanium">Cost Reduction</div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-platinum shadow-sm">
                  <div className="text-3xl font-bold text-primary-red mb-1">24/7</div>
                  <div className="text-sm text-titanium">Monitoring</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is MLOps */}
      <section className="py-20 bg-white">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal">
                  What is <span className="text-gradient">MLOps?</span>
                </h2>
                <p className="text-lg text-titanium mb-6 leading-relaxed">
                  Machine Learning Operations (MLOps) is a set of practices that combines machine learning, DevOps, and data engineering to deploy and maintain ML systems in production reliably and efficiently.
                </p>
                <p className="text-titanium mb-6">
                  Our MLOps Framework provides the infrastructure, tools, and processes needed to streamline the entire ML lifecycle—from experimentation and model development to deployment, monitoring, and continuous improvement.
                </p>
                <div className="space-y-4">
                  {[
                    'Automated ML pipelines for faster iterations',
                    'Robust model versioning and experiment tracking',
                    'Seamless deployment to multiple environments',
                    'Real-time monitoring and performance analytics',
                    'Automated retraining and continuous learning',
                    'Enterprise-grade security and governance'
                  ].map((item, index) => (
                    <div key={index} className="flex items-start">
                      <svg className="w-6 h-6 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-graphite">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="bg-alabaster rounded-2xl p-8 border border-platinum">
                  <h3 className="text-xl font-bold mb-6 text-charcoal">MLOps Lifecycle</h3>
                  <div className="space-y-4">
                    {[
                      { step: '1', title: 'Data Collection & Preparation', color: 'bg-primary-red' },
                      { step: '2', title: 'Model Development & Training', color: 'bg-deep-garnet' },
                      { step: '3', title: 'Model Validation & Testing', color: 'bg-coral-mist' },
                      { step: '4', title: 'Deployment & Serving', color: 'bg-primary-red' },
                      { step: '5', title: 'Monitoring & Maintenance', color: 'bg-deep-garnet' },
                      { step: '6', title: 'Retraining & Optimization', color: 'bg-coral-mist' }
                    ].map((phase, index) => (
                      <div key={index} className="flex items-center bg-white p-4 rounded-lg border border-platinum group hover:shadow-md transition-all duration-300">
                        <div className={`w-10 h-10 ${phase.color} rounded-full flex items-center justify-center text-white font-bold mr-4 flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                          {phase.step}
                        </div>
                        <span className="font-medium text-charcoal">{phase.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="py-20 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal">
              Core MLOps <span className="text-gradient">Capabilities</span>
            </h2>
            <p className="text-xl text-titanium max-w-3xl mx-auto">
              Comprehensive tools and practices to operationalize your machine learning at enterprise scale
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {[
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                ),
                title: 'Data Pipeline Management',
                description: 'Automated data ingestion, validation, and preprocessing pipelines with version control and lineage tracking.'
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                ),
                title: 'Model Training Orchestration',
                description: 'Scalable training infrastructure with hyperparameter tuning, distributed computing, and resource optimization.'
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                ),
                title: 'Experiment Tracking',
                description: 'Comprehensive tracking of experiments, metrics, parameters, and artifacts with easy comparison and visualization.'
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                ),
                title: 'Model Registry & Versioning',
                description: 'Centralized model repository with versioning, metadata management, and lifecycle stage tracking.'
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                ),
                title: 'Deployment Automation',
                description: 'One-click deployment to multiple environments with blue-green deployments, canary releases, and rollback capabilities.'
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                ),
                title: 'Performance Monitoring',
                description: 'Real-time monitoring of model performance, data drift, prediction latency, and system health metrics.'
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                ),
                title: 'Automated Retraining',
                description: 'Trigger-based model retraining with performance threshold monitoring and automated pipeline execution.'
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                ),
                title: 'Security & Compliance',
                description: 'Built-in security controls, audit logging, access management, and compliance with industry standards.'
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                ),
                title: 'Collaboration Tools',
                description: 'Team collaboration features with role-based access, shared workspaces, and integrated communication.'
              }
            ].map((capability, index) => (
              <div key={index} className="card group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-14 h-14 bg-blush-light/40 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary-red group-hover:scale-110 transition-all duration-300">
                  <svg className="w-7 h-7 text-primary-red group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {capability.icon}
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-charcoal group-hover:text-primary-red transition-colors duration-300">
                  {capability.title}
                </h3>
                <p className="text-titanium leading-relaxed">
                  {capability.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-20 bg-white">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal">
                Technology <span className="text-gradient">Stack</span>
              </h2>
              <p className="text-xl text-titanium max-w-3xl mx-auto">
                Built on industry-leading tools and frameworks, customized for enterprise requirements
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="card">
                <h3 className="text-xl font-bold mb-4 text-charcoal flex items-center">
                  <svg className="w-6 h-6 text-primary-red mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                  ML Frameworks & Tools
                </h3>
                <div className="space-y-3">
                  {[
                    { name: 'MLflow', desc: 'Experiment tracking and model registry' },
                    { name: 'Kubeflow', desc: 'ML workflow orchestration on Kubernetes' },
                    { name: 'TensorFlow Extended (TFX)', desc: 'End-to-end ML platform' },
                    { name: 'Apache Airflow', desc: 'Pipeline orchestration and scheduling' },
                    { name: 'DVC', desc: 'Data and model versioning' }
                  ].map((tool, index) => (
                    <div key={index} className="flex items-start p-3 bg-alabaster rounded-lg hover:bg-blush-light/30 transition-colors duration-300">
                      <div className="w-2 h-2 bg-primary-red rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <div className="font-semibold text-charcoal">{tool.name}</div>
                        <div className="text-sm text-titanium">{tool.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card">
                <h3 className="text-xl font-bold mb-4 text-charcoal flex items-center">
                  <svg className="w-6 h-6 text-primary-red mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                  </svg>
                  Infrastructure & Deployment
                </h3>
                <div className="space-y-3">
                  {[
                    { name: 'Kubernetes', desc: 'Container orchestration platform' },
                    { name: 'Docker', desc: 'Containerization and packaging' },
                    { name: 'AWS SageMaker', desc: 'Managed ML service' },
                    { name: 'Prometheus & Grafana', desc: 'Monitoring and visualization' },
                    { name: 'Terraform', desc: 'Infrastructure as code' }
                  ].map((tool, index) => (
                    <div key={index} className="flex items-start p-3 bg-alabaster rounded-lg hover:bg-blush-light/30 transition-colors duration-300">
                      <div className="w-2 h-2 bg-primary-red rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <div className="font-semibold text-charcoal">{tool.name}</div>
                        <div className="text-sm text-titanium">{tool.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Approach */}
      <section className="py-20 bg-gradient-to-br from-alabaster to-pearl-white">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal">
                Our Implementation <span className="text-gradient">Approach</span>
              </h2>
              <p className="text-xl text-titanium max-w-3xl mx-auto">
                Structured methodology to ensure successful MLOps adoption and ROI
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  phase: 'Phase 1',
                  title: 'Assessment & Planning',
                  duration: '2-3 weeks',
                  items: [
                    'Current state analysis',
                    'Requirements gathering',
                    'Architecture design',
                    'Tool selection'
                  ]
                },
                {
                  phase: 'Phase 2',
                  title: 'Foundation Setup',
                  duration: '3-4 weeks',
                  items: [
                    'Infrastructure provisioning',
                    'Tool integration',
                    'Security configuration',
                    'Pipeline templates'
                  ]
                },
                {
                  phase: 'Phase 3',
                  title: 'Pilot Implementation',
                  duration: '4-6 weeks',
                  items: [
                    'First model pipeline',
                    'Monitoring setup',
                    'Team training',
                    'Process refinement'
                  ]
                },
                {
                  phase: 'Phase 4',
                  title: 'Scale & Optimize',
                  duration: 'Ongoing',
                  items: [
                    'Additional models',
                    'Performance tuning',
                    'Best practices',
                    'Continuous improvement'
                  ]
                }
              ].map((phase, index) => (
                <div key={index} className="relative">
                  {/* Connection line */}
                  {index < 3 && (
                    <div className="hidden lg:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-coral-mist to-transparent"></div>
                  )}
                  
                  <div className="card bg-white hover:shadow-xl transition-all duration-300 relative z-10">
                    <div className="inline-block px-3 py-1 bg-primary-red/10 text-primary-red rounded-full text-sm font-semibold mb-3">
                      {phase.phase}
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-charcoal">{phase.title}</h3>
                    <p className="text-sm text-titanium mb-4">{phase.duration}</p>
                    <ul className="space-y-2">
                      {phase.items.map((item, idx) => (
                        <li key={idx} className="flex items-center text-sm text-graphite">
                          <svg className="w-4 h-4 text-primary-red mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal">
                Business <span className="text-gradient">Benefits</span>
              </h2>
              <p className="text-xl text-titanium max-w-3xl mx-auto">
                Measurable impact on your ML operations and business outcomes
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: 'Accelerated Time-to-Market',
                  metrics: '85% faster deployment',
                  description: 'Reduce model deployment time from months to days with automated pipelines and standardized workflows.'
                },
                {
                  title: 'Improved Model Quality',
                  metrics: '40% better accuracy',
                  description: 'Systematic experiment tracking and validation ensures only high-quality models reach production.'
                },
                {
                  title: 'Enhanced Reliability',
                  metrics: '99.9% uptime',
                  description: 'Robust monitoring, automated testing, and rollback capabilities ensure consistent model performance.'
                },
                {
                  title: 'Cost Optimization',
                  metrics: '60% cost reduction',
                  description: 'Efficient resource utilization, automated scaling, and optimized infrastructure reduce operational costs.'
                },
                {
                  title: 'Better Collaboration',
                  metrics: '3x productivity gain',
                  description: 'Shared platforms and standardized processes enable seamless collaboration between data scientists and engineers.'
                },
                {
                  title: 'Compliance & Governance',
                  metrics: '100% audit trail',
                  description: 'Complete lineage tracking, access controls, and audit logs ensure regulatory compliance and model governance.'
                }
              ].map((benefit, index) => (
                <div key={index} className="card group hover:shadow-xl transition-all duration-300">
                  <div className="text-3xl font-bold text-primary-red mb-3 group-hover:scale-110 transition-transform duration-300">
                    {benefit.metrics}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-charcoal">
                    {benefit.title}
                  </h3>
                  <p className="text-titanium">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-alabaster">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal">
                Industry <span className="text-gradient">Use Cases</span>
              </h2>
              <p className="text-xl text-titanium max-w-3xl mx-auto">
                Real-world applications across various industries
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  industry: 'Financial Services',
                  useCase: 'Fraud Detection Models',
                  description: 'Deploy and monitor real-time fraud detection models processing millions of transactions daily with automated retraining based on new fraud patterns.',
                  impact: 'Reduced fraud losses by 45% with 99.9% uptime'
                },
                {
                  industry: 'Healthcare',
                  useCase: 'Diagnostic AI Systems',
                  description: 'Operationalize medical imaging models with strict compliance requirements, version control, and continuous performance monitoring.',
                  impact: 'Improved diagnostic accuracy by 35% while maintaining HIPAA compliance'
                },
                {
                  industry: 'Retail & E-commerce',
                  useCase: 'Personalization Engines',
                  description: 'Manage multiple recommendation models across different product categories with A/B testing and automated performance optimization.',
                  impact: 'Increased conversion rates by 28% with 50% faster model updates'
                },
                {
                  industry: 'Manufacturing',
                  useCase: 'Predictive Maintenance',
                  description: 'Deploy IoT-based predictive maintenance models with edge computing support and centralized monitoring across facilities.',
                  impact: 'Reduced equipment downtime by 40% and maintenance costs by 30%'
                }
              ].map((useCase, index) => (
                <div key={index} className="card bg-white hover:shadow-xl transition-all duration-300">
                  <div className="inline-block px-3 py-1 bg-coral-mist/20 text-primary-red rounded-full text-sm font-semibold mb-4">
                    {useCase.industry}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-charcoal">{useCase.useCase}</h3>
                  <p className="text-titanium mb-4">{useCase.description}</p>
                  <div className="pt-4 border-t border-platinum">
                    <div className="flex items-center text-sm">
                      <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                      <span className="font-semibold text-charcoal">{useCase.impact}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Integration Ecosystem */}
      <section className="py-20 bg-white">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal">
                Integration <span className="text-gradient">Ecosystem</span>
              </h2>
              <p className="text-xl text-titanium max-w-3xl mx-auto">
                Seamlessly integrate with your existing technology stack
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
              {[
                { category: 'Data Sources', items: ['PostgreSQL', 'MongoDB', 'S3', 'Snowflake', 'BigQuery'] },
                { category: 'ML Frameworks', items: ['TensorFlow', 'PyTorch', 'Scikit-learn', 'XGBoost', 'Keras'] },
                { category: 'Cloud Platforms', items: ['AWS', 'Azure', 'GCP', 'On-Premise', 'Hybrid'] },
                { category: 'Dev Tools', items: ['Git', 'Jenkins', 'GitHub Actions', 'Jira', 'Slack'] }
              ].map((ecosystem, index) => (
                <div key={index} className="card hover:shadow-lg transition-all duration-300">
                  <h3 className="font-bold text-charcoal mb-4 pb-3 border-b border-platinum">{ecosystem.category}</h3>
                  <ul className="space-y-2">
                    {ecosystem.items.map((item, idx) => (
                      <li key={idx} className="text-sm text-titanium flex items-center">
                        <span className="w-1.5 h-1.5 bg-primary-red rounded-full mr-2"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-red to-deep-garnet text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '30px 30px'
          }}></div>
        </div>

        <div className="page-container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Ready to Transform Your ML Operations?
            </h2>
            <p className="text-xl mb-8 opacity-90 leading-relaxed">
              Let's discuss how our MLOps framework can accelerate your machine learning initiatives and drive measurable business results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1">
                Schedule Consultation
              </Link>
              <Link to="/ai-hub" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300">
                Explore More AI Solutions
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-alabaster">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-charcoal text-center">
              Related <span className="text-gradient">AI Solutions</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Link to="/ai-hub/intelligent-automation" className="card hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-charcoal">Intelligent Automation</h3>
                <p className="text-titanium mb-4">Automate complex workflows with AI-powered decision-making and process optimization.</p>
                <span className="text-primary-red font-semibold">Learn More →</span>
              </Link>

              <Link to="/ai-hub/ai-ethics-governance" className="card hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-charcoal">AI Ethics & Governance</h3>
                <p className="text-titanium mb-4">Ensure responsible AI deployment with comprehensive governance frameworks.</p>
                <span className="text-primary-red font-semibold">Learn More →</span>
              </Link>

              <Link to="/services/ai-machine-learning" className="card hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-charcoal">AI & Machine Learning</h3>
                <p className="text-titanium mb-4">Custom AI solutions tailored to your specific business challenges and objectives.</p>
                <span className="text-primary-red font-semibold">Learn More →</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default MLOpsFramework;